import { useEffect, useState } from 'react';
import { Radio, RefreshCw } from 'lucide-react';
import { Button } from '@/components/Button';
import { useAuth } from '@/hooks/useAuth';
import { facebookLeadFormsService, LeadPageSubscription } from '@/services/facebook-lead-forms.service';

export function LeadPageSubscriptionCard({ pageId }: { pageId: string }) {
  const { user } = useAuth();
  const [state, setState] = useState<LeadPageSubscription | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    let active = true;
    setBusy(true);
    facebookLeadFormsService.subscription(pageId).then(value => { if (active) setState(value); })
      .catch(() => { if (active) setError('Could not check lead delivery. Refresh to retry.'); })
      .finally(() => { if (active) setBusy(false); });
    return () => { active = false; };
  }, [pageId]);
  const check = async (repair = false) => {
    setBusy(true); setError('');
    try { setState(await facebookLeadFormsService.subscription(pageId, repair)); }
    catch { setError('Could not verify lead delivery. Check Page access and retry.'); }
    finally { setBusy(false); }
  };
  const verified = !error && state?.subscribed;
  return <section className="rounded-xl border border-border bg-surface p-4 flex flex-wrap items-center gap-4">
    <Radio size={20} className="text-primary shrink-0" />
    <div className="flex-1 min-w-52">
      <h2 className="text-sm font-semibold text-text-primary">Lead delivery · {busy ? 'Checking' : verified ? 'Page subscribed' : 'Needs attention'}</h2>
      <p role="status" className="mt-1 text-xs text-text-muted">{error || state?.error || (verified
        ? 'This Page sends lead events to the connected app. A fresh form submission is still needed to verify delivery into the CRM.'
        : 'Connect this Page to receive new lead-form submissions in SlightorCRM.')}</p>
      {state?.checkedAt && <p className="mt-1 text-2xs text-text-muted">Checked {new Date(state.checkedAt).toLocaleString()}</p>}
    </div>
    <Button size="sm" variant="secondary" loading={busy} iconLeft={<RefreshCw size={13} />} onClick={() => check()}>Check</Button>
    {!verified && user?.role === 'company_admin' && <Button size="sm" loading={busy} onClick={() => check(true)}>Connect lead delivery</Button>}
  </section>;
}
